import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { getMissingLocateSkillConfig } from '../../util/locateSkill';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

/** Join labels into a readable list: "a", "a and b", "a, b, and c". */
function humanJoin(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;

  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Blocks a `RunLocateSkill` step whose referenced skill is missing capture
 * pose, teach configuration, or reference plane. Skips when `skillId` is empty
 * (the step validator flags that), the skill isn't in `ctx.skills` (not loaded
 * yet — avoid false positives), or it isn't a Locate skill.
 */
class RunLocateSkillRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunLocateSkill', 'RunLocateSkill');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    const a = args as Extract<
      RcStep.Arguments,
      { argumentKind: 'RunLocateSkill' }
    >;

    if (!a.skillId) return;

    const skill = this.findSkill(ctx, a.skillId);
    if (!skill || skill.skillType !== 'locate') return;

    const missing = getMissingLocateSkillConfig({
      shared: skill.shared as Record<string, unknown>,
      method: skill.method,
      methodConfig: skill.methodConfig as Record<string, unknown>,
    });

    const labels: string[] = [];
    if (missing.capturePose) labels.push('capture pose');
    if (missing.teach) labels.push('teach configuration');
    if (missing.referencePlane) labels.push('reference plane');

    if (labels.length === 0) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `This Locate skill needs ${humanJoin(
        labels,
      )} before the routine can run.`,
      fieldId: 'skillId',
    });
  }
}

export const RunLocateSkillValidation: ValidationRule =
  new RunLocateSkillRule().toValidationRule();
